import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ChatWidget, BackToTop } from '../components/ChatAndUtils';
import { Button } from '../components/ui/button';
import {
  Upload, FileText, Download, Trash2, Search, X, ArrowLeft, Loader2, AlertCircle, CheckCircle2,
  FileSpreadsheet, FileImage, File as FileIcon, Archive, ScanLine
} from 'lucide-react';
import { api, API } from '../context/AuthContext';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import { TeamDokZuordnung, refFromSel } from '../components/register/TeamDokZuordnung';

const ALLOWED_EXTENSIONS = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.csv', '.jpg', '.jpeg', '.png', '.heic', '.tif', '.tiff', '.eml', '.msg', '.zip'];
const MAX_SIZE = 50 * 1024 * 1024;
const MERGEABLE = ['.pdf', '.jpg', '.jpeg', '.png', '.heic', '.tif', '.tiff'];
const extOf = (f) => '.' + (f.name.split('.').pop() || '').toLowerCase();

const getIcon = (filename) => {
  const ext = filename.toLowerCase().split('.').pop();
  if (['pdf', 'doc', 'docx'].includes(ext)) return FileText;
  if (['xls', 'xlsx', 'csv'].includes(ext)) return FileSpreadsheet;
  if (['jpg', 'jpeg', 'png', 'heic', 'tif', 'tiff'].includes(ext)) return FileImage;
  if (ext === 'zip') return Archive;
  return FileIcon;
};

const formatSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const Dokumente = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const isTeam = ['admin', 'kanzlei_mitarbeiter'].includes(user?.role);
  const [regSel, setRegSel] = useState(null);
  const { toast } = useToast();
  const fileInputRef = useRef(null);

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showUpload, setShowUpload] = useState(searchParams.get('upload') === '1');
  const [uploadFiles, setUploadFiles] = useState([]);
  const [merge, setMerge] = useState(true);
  const [mergedName, setMergedName] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [description, setDescription] = useState('');
  const [enthaeltFrist, setEnthaeltFrist] = useState(false);
  const [category, setCategory] = useState('');
  const [mandateRef, setMandateRef] = useState('');
  const [error, setError] = useState('');

  const fetchDocs = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/documents');
      setDocuments(data);
    } catch (e) {
      toast({ title: t('dokumente.toast.error'), description: t('dokumente.toast.fetch_failed'), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast, t]);

  useEffect(() => {
    fetchDocs();
  }, [fetchDocs]);

  useEffect(() => {
    if (!documents.some((d) => ['laeuft', 'wartet'].includes(d.ocr_status))) return undefined;
    const id = setTimeout(() => api.get('/documents').then((r) => setDocuments(r.data)).catch(() => {}), 6000);
    return () => clearTimeout(id);
  }, [documents]);

  const handleFileSelect = (fileList) => {
    setError('');
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const bad = files.find((f) => !ALLOWED_EXTENSIONS.includes(extOf(f)));
    if (bad) { setError(t('dokumente.upload.error_filetype', { ext: extOf(bad), name: bad.name, allowed: ALLOWED_EXTENSIONS.join(', ') })); return; }
    const big = files.find((f) => f.size > MAX_SIZE);
    if (big) { setError(t('dokumente.upload.error_filesize', { name: big.name, size: formatSize(big.size) })); return; }
    setUploadFiles((prev) => {
      const seen = new Set(prev.map((f) => f.name + f.size));
      return [...prev, ...files.filter((f) => !seen.has(f.name + f.size))].slice(0, 30);
    });
  };
  const removeFile = (idx) => setUploadFiles((prev) => prev.filter((_, i) => i !== idx));
  const moveFile = (idx, dir) => setUploadFiles((prev) => {
    const next = [...prev]; const j = idx + dir;
    if (j < 0 || j >= next.length) return prev;
    [next[idx], next[j]] = [next[j], next[idx]];
    return next;
  });
  const mergeable = uploadFiles.filter((f) => MERGEABLE.includes(extOf(f)));
  const canMerge = uploadFiles.length > 1 && mergeable.length > 1;

  const resetUpload = () => { setUploadFiles([]); setMerge(true); setMergedName(''); setDescription(''); setEnthaeltFrist(false); setCategory(''); setMandateRef(''); setRegSel(null); };

  const handleUpload = async () => {
    if (!uploadFiles.length) return;
    if (!mandateRef.trim() || (isTeam && !regSel?.mandanten_nr)) {
      toast({ title: t('dokumente.toast.mandatory_field'), description: t('dokumente.upload.mandate_ref_required'), variant: 'destructive' });
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    setError('');
    const fd = new FormData();
    uploadFiles.forEach((f) => fd.append('files', f));
    fd.append('mandate_ref', mandateRef.trim());
    if (isTeam && regSel?.mandanten_nr) { fd.append('register_mnr', regSel.mandanten_nr); fd.append('register_case_id', regSel.case_id || ''); }
    fd.append('merge', String(merge && canMerge));
    if (merge && mergedName.trim()) fd.append('merged_name', mergedName.trim());
    if (description) fd.append('description', description);
    if (!isTeam && enthaeltFrist) fd.append('enthaelt_frist', 'true');
    if (category) fd.append('category', category);
    try {
      const { data } = await api.post('/documents/upload-multi', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (e.total) setUploadProgress(Math.round((e.loaded * 100) / e.total));
        },
      });
      toast({ 
        title: t('dokumente.toast.upload_success'), 
        description: data.merged 
          ? t('dokumente.toast.upload_merged', { count: mergeable.length, additional: data.count > 1 ? ` (+${data.count - 1} ${t('dokumente.toast.additional')})` : '' }) 
          : t('dokumente.toast.upload_files', { count: data.count })
      });
      resetUpload();
      setShowUpload(false);
      fetchDocs();
    } catch (err) {
      const d = err.response?.data?.detail;
      setError((Array.isArray(d) ? d.map((x) => x.msg).join(', ') : d) || t('dokumente.upload.error_failed'));
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(t('dokumente.list.delete_confirm', { filename: doc.filename }))) return;
    try {
      await api.delete(`/documents/${doc.doc_id}`);
      toast({ title: t('dokumente.toast.deleted'), description: doc.filename });
      fetchDocs();
    } catch (e) {
      toast({ title: t('dokumente.toast.error'), description: t('dokumente.toast.delete_failed'), variant: 'destructive' });
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await api.get(`/documents/${doc.doc_id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast({ title: t('dokumente.toast.error'), description: t('dokumente.toast.download_failed'), variant: 'destructive' });
    }
  };

  const filtered = documents.filter(
    (d) =>
      !search ||
      d.filename.toLowerCase().includes(search.toLowerCase()) ||
      (d.description || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="pt-[104px] lg:pt-[152px] pb-20 bg-[#f5f8fa] min-h-[80vh]">
        <section className="py-10">
          <div className="max-w-5xl mx-auto px-6 lg:px-10">
            <Link to="/dashboard" className="inline-flex items-center gap-1 text-[#34536b] text-sm hover:text-[#b8893d] mb-4">
              <ArrowLeft className="w-3.5 h-3.5" /> {t('dokumente.back_to_dashboard')}
            </Link>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
              <div>
                <h1 className="font-serif text-3xl md:text-4xl text-[#1B3A5C]">{t('dokumente.title')}</h1>
                <p className="text-[#34536b] text-sm mt-1">{t('dokumente.subtitle', { count: documents.length })}</p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Link to="/dokumente/scannen" data-testid="docs-scan-link">
                  <Button variant="outline" className="border-[#1B3A5C] text-[#1B3A5C] hover:bg-[#1B3A5C] hover:text-white tracking-wider text-xs">
                    <ScanLine className="mr-2 w-4 h-4" /> {t('dokumente.button_scan')}
                  </Button>
                </Link>
                <Button onClick={() => setShowUpload(true)} data-testid="docs-upload-open" className="bg-[#b8893d] hover:bg-[#a07730] text-white tracking-wider text-xs">
                  <Upload className="mr-2 w-4 h-4" /> {t('dokumente.button_upload')}
                </Button>
              </div>
            </div>

            {/* Upload modal */}
            {showUpload && (
              <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => !uploading && setShowUpload(false)}>
                <div className="bg-white rounded-sm w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                  <div className="bg-[#1B3A5C] text-white px-6 py-4 flex items-center justify-between">
                    <h3 className="font-serif text-xl">{t('dokumente.upload.modal_title')}</h3>
                    <button onClick={() => !uploading && setShowUpload(false)} data-testid="upload-modal-close" className="hover:bg-white/10 p-1 rounded">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="p-6 space-y-4">
                    {error && (
                      <div className="flex items-start gap-2 p-3 bg-rose-50 border-l-4 border-rose-500 text-rose-700 text-sm">
                        <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span>{error}</span>
                      </div>
                    )}
                    <label
                      onDrop={(e) => { e.preventDefault(); handleFileSelect(e.dataTransfer.files); }}
                      onDragOver={(e) => e.preventDefault()}
                      className="flex flex-col items-center justify-center py-8 px-6 bg-[#fafaf7] border-2 border-dashed border-gray-300 hover:border-[#b8893d] cursor-pointer transition-colors"
                      data-testid="upload-dropzone"
                    >
                      <Upload className="w-10 h-10 text-[#b8893d] mb-3" />
                      <p className="text-[#1B3A5C] font-medium">{uploadFiles.length ? t('dokumente.upload.add_more') : t('dokumente.upload.select_or_drag')}</p>
                      <p className="text-[#34536b] text-xs mt-1">{t('dokumente.upload.allowed_types')}</p>
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept={ALLOWED_EXTENSIONS.join(',')}
                        className="hidden"
                        onChange={(e) => { handleFileSelect(e.target.files); e.target.value = ''; }}
                        data-testid="upload-file-input"
                      />
                    </label>
                    {uploadFiles.length > 0 && (
                      <ul className="divide-y divide-gray-100 border border-gray-100 rounded-sm" data-testid="upload-file-list">
                        {uploadFiles.map((f, i) => (
                          <li key={f.name + f.size} className="flex items-center gap-2 px-3 py-2 text-sm">
                            <span className="w-5 text-[11px] text-[#9aa5b1]">{i + 1}.</span>
                            <span className="flex-1 truncate text-[#1B3A5C]" title={f.name}>{f.name}</span>
                            <span className="text-[11px] text-[#7a8a99] whitespace-nowrap">{formatSize(f.size)}</span>
                            {merge && canMerge && MERGEABLE.includes(extOf(f)) && (
                              <>
                                <button type="button" onClick={() => moveFile(i, -1)} disabled={i === 0} className="p-1 text-[#7a8a99] hover:text-[#1B3A5C] disabled:opacity-30" aria-label={t('dokumente.upload.move_up')} data-testid={`upload-move-up-${i}`}>▲</button>
                                <button type="button" onClick={() => moveFile(i, 1)} disabled={i === uploadFiles.length - 1} className="p-1 text-[#7a8a99] hover:text-[#1B3A5C] disabled:opacity-30" aria-label={t('dokumente.upload.move_down')} data-testid={`upload-move-down-${i}`}>▼</button>
                              </>
                            )}
                            <button type="button" onClick={() => removeFile(i)} className="p-1 text-[#7a8a99] hover:text-rose-600" aria-label={t('dokumente.upload.remove')} data-testid={`upload-remove-${i}`}><X className="w-3.5 h-3.5" /></button>
                          </li>
                        ))}
                      </ul>
                    )}
                    {canMerge && (
                      <div className={`p-3 border rounded-sm ${merge ? 'border-[#b8893d] bg-[#fbf7f0]' : 'border-gray-200'}`} data-testid="upload-merge-box">
                        <label className="flex items-start gap-3 cursor-pointer text-sm text-[#34536b]">
                          <input type="checkbox" className="mt-0.5 w-4 h-4 accent-[#b8893d]" checked={merge} onChange={(e) => setMerge(e.target.checked)} data-testid="upload-merge" />
                          <span><strong className="text-[#1B3A5C]">{t('dokumente.upload.merge_title')}</strong> – {t('dokumente.upload.merge_description', { count: mergeable.length, suffix: mergeable.length < uploadFiles.length ? t('dokumente.upload.merge_suffix') : '' })}</span>
                        </label>
                        {merge && (
                          <input placeholder={t('dokumente.upload.merged_name_placeholder')} value={mergedName} onChange={(e) => setMergedName(e.target.value)} data-testid="upload-merged-name"
                            className="mt-2 w-full px-3 py-2 bg-white border border-gray-200 focus:border-[#b8893d] focus:outline-none text-sm rounded-sm" />
                        )}
                      </div>
                    )}
                    {isTeam ? (
                      <TeamDokZuordnung value={regSel} onChange={(v) => { setRegSel(v); setMandateRef(refFromSel(v)); }} testId="upload-zuordnung" />
                    ) : (
                    <input
                      placeholder={t('dokumente.upload.mandate_ref_placeholder')}
                      value={mandateRef}
                      onChange={(e) => setMandateRef(e.target.value)}
                      data-testid="input-mandateRef"
                      required
                      className="w-full px-4 py-2.5 bg-white border border-[#b8893d]/40 focus:border-[#b8893d] focus:outline-none focus:ring-2 focus:ring-[#b8893d]/20 text-sm rounded-sm font-medium"
                    />
                    )}
                    <input
                      placeholder={t('dokumente.upload.category_placeholder')}
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                      className="w-full px-4 py-2.5 bg-white border border-gray-200 focus:border-[#b8893d] focus:outline-none focus:ring-2 focus:ring-[#b8893d]/20 text-sm rounded-sm"
                    />
                    <textarea
                      rows={3}
                      placeholder={t('dokumente.upload.description_placeholder')}
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      className="w-full px-4 py-2.5 bg-white border border-gray-200 focus:border-[#b8893d] focus:outline-none focus:ring-2 focus:ring-[#b8893d]/20 text-sm rounded-sm resize-none"
                    />
                    {!isTeam && (
                      <label className="flex items-start gap-2 text-sm text-[#1B3A5C] cursor-pointer" data-testid="upload-enthaelt-frist-label">
                        <input type="checkbox" checked={enthaeltFrist} onChange={(e) => setEnthaeltFrist(e.target.checked)} className="mt-1 accent-[#b8893d]" data-testid="upload-enthaelt-frist" />
                        <span>{t('dokumente.upload.enthaelt_frist', 'Das Schreiben enthält eine Frist')}</span>
                      </label>
                    )}
                    {uploading && (
                      <div>
                        <div className="flex justify-between text-xs text-[#34536b] mb-1">
                          <span>{t('dokumente.upload.uploading')}</span>
                          <span>{uploadProgress} %</span>
                        </div>
                        <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div className="h-full bg-[#b8893d] transition-all" style={{ width: `${uploadProgress}%` }}></div>
                        </div>
                      </div>
                    )}
                    <div className="flex gap-3 pt-2">
                      <Button
                        type="button"
                        onClick={() => setShowUpload(false)}
                        disabled={uploading}
                        variant="outline"
                        className="flex-1 border-[#1B3A5C] text-[#1B3A5C] hover:bg-[#1B3A5C] hover:text-white tracking-wider text-xs"
                      >
                        {t('dokumente.upload.button_cancel')}
                      </Button>
                      <Button
                        type="button"
                        onClick={handleUpload}
                        disabled={!uploadFiles.length || uploading}
                        data-testid="upload-submit"
                        className="flex-1 bg-[#b8893d] hover:bg-[#a07730] text-white tracking-wider text-xs disabled:opacity-40"
                      >
                        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : uploadFiles.length > 1 ? t('dokumente.upload.button_upload_multiple', { count: uploadFiles.length }) : t('dokumente.upload.button_upload_single')}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Search */}
            <div className="relative mb-6">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#34536b]" />
              <input
                placeholder={t('dokumente.search_placeholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-11 pr-4 py-3 bg-white border border-gray-200 focus:border-[#b8893d] focus:outline-none focus:ring-2 focus:ring-[#b8893d]/20 text-sm rounded-sm"
              />
            </div>

            {/* List */}
            {loading ? (
              <div className="text-center py-16">
                <Loader2 className="w-8 h-8 text-[#b8893d] animate-spin mx-auto" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="bg-white p-12 text-center rounded-sm border-2 border-dashed border-gray-200">
                <FileText className="w-12 h-12 text-[#b8893d]/40 mx-auto mb-4" />
                <h3 className="text-[#1B3A5C] font-serif text-xl mb-2">
                  {search ? t('dokumente.list.no_results') : t('dokumente.list.no_documents')}
                </h3>
                <p className="text-[#34536b] text-sm mb-6">
                  {search ? t('dokumente.list.try_another_search') : t('dokumente.list.upload_first')}
                </p>
                {!search && (
                  <Button onClick={() => setShowUpload(true)} className="bg-[#b8893d] hover:bg-[#a07730] text-white tracking-wider text-xs">
                    <Upload className="mr-2 w-3.5 h-3.5" /> {t('dokumente.list.button_upload_first')}
                  </Button>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-sm shadow-sm overflow-hidden">
                {filtered.map((d, idx) => {
                  const Icon = getIcon(d.filename);
                  return (
                    <div
                      key={d.doc_id}
                      className={`flex items-center gap-4 p-4 hover:bg-[#fafaf7] transition-colors ${idx > 0 ? 'border-t border-gray-100' : ''}`}
                    >
                      <div className="w-11 h-11 bg-[#f5f8fa] rounded-sm flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-[#b8893d]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-[#1B3A5C] font-medium text-sm truncate">{d.filename}
                            {d.ocr_status === 'fertig' && <span className="ml-2 align-middle text-[9px] tracking-wider px-1.5 py-0.5 bg-emerald-50 text-emerald-700" data-testid={`doc-ocr-${d.doc_id}`}>{t('dokumente.ocr_fertig')}</span>}
                            {['laeuft', 'wartet'].includes(d.ocr_status) && <span className="ml-2 align-middle text-[9px] tracking-wider px-1.5 py-0.5 bg-amber-50 text-amber-700" data-testid={`doc-ocr-${d.doc_id}`}>{t('dokumente.ocr_laeuft')}</span>}
                          </p>
                          <span className="text-[10px] bg-[#b8893d]/10 text-[#b8893d] px-2 py-0.5 rounded-sm tracking-wider">
                            {d.uploaded_by === 'kanzlei' ? t('dokumente.list.badge_kanzlei') : t('dokumente.list.badge_me')}
                          </span>
                          {d.category && (
                            <span className="text-[10px] bg-[#1B3A5C]/10 text-[#1B3A5C] px-2 py-0.5 rounded-sm tracking-wider">
                              {d.category}
                            </span>
                          )}
                        </div>
                        <p className="text-[#34536b] text-xs mt-0.5">
                          {formatSize(d.size)} • {new Date(d.created_at).toLocaleString('de-DE')}
                        </p>
                        {d.description && <p className="text-[#34536b] text-xs mt-1 italic line-clamp-1">{d.description}</p>}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={() => handleDownload(d)}
                          className="w-9 h-9 flex items-center justify-center text-[#1B3A5C] hover:bg-[#f5f8fa] rounded-sm"
                          aria-label={t('dokumente.list.button_download')}
                          title={t('dokumente.list.button_download_title')}
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        {d.loeschbar !== false && <button
                          onClick={() => handleDelete(d)}
                          className="w-9 h-9 flex items-center justify-center text-rose-600 hover:bg-rose-50 rounded-sm"
                          aria-label={t('dokumente.list.button_delete')}
                          title={t('dokumente.list.button_delete_title')}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
      <ChatWidget />
      <BackToTop />
      <Toaster />
    </div>
  );
};

export default Dokumente;
